import { sortBy as _sortBy } from 'lodash-es';
import { create } from 'zustand';
import type { HcpAssociation } from '@/models/hcp/association';
import type { HCO } from '@/models/hcp/hco';
import type { HCPExtended } from '@/models/hcp/hcp';
import type { Newsfeed } from '@/models/newsfeed/newsfeed';
import type { HcpSectionType } from '@/pages/HCPOverview/tabs/tabsConstants';

interface LoadingState {
  hcp: boolean;
  locations: boolean;
  associations: boolean;
  newsfeed: boolean;
  topicScores: boolean;
  activityScores: boolean;
  clinicalTrialSponsors: boolean;
}

interface HCPOverviewState {
  tabIndex: HcpSectionType;
  hcp: HCPExtended | null;
  primaryLocation: HCO | null;
  otherLocations: HCO[];
  associations: HcpAssociation[];
  newsfeed: Newsfeed[];
  loading: LoadingState;
}

interface HCPOverviewActions {
  updateTabIndex: (tabIndex: HcpSectionType) => void;
  getFullName: () => string;
  updateHcp: (hcp: HCPExtended | null) => void;
  setNewsfeed: (newsfeed: Newsfeed[]) => void;
  setLoading: (key: keyof LoadingState, loading: boolean) => void;
}

export type HcpOverviewStoreState = HCPOverviewState & HCPOverviewActions;

const useHcpOverviewStore = create<HCPOverviewState & HCPOverviewActions>((set, get) => ({
  tabIndex: 'overview',
  updateTabIndex: (tabIndex: HcpSectionType) => set(() => ({ tabIndex })),
  hcp: null,
  getFullName: () => {
    const hcp = get().hcp;
    if (!hcp) return '';
    return `${hcp.person.firstName ?? ''} ${hcp.person.lastName ?? ''}`.trim();
  },
  updateHcp: (hcp: HCPExtended | null) =>
    set(() => ({
      hcp,
      primaryLocation: hcp?.hcoLocations?.find(hcoLocation => hcoLocation.location?.primary) ?? null,
      otherLocations: _sortBy(
        hcp?.hcoLocations?.filter(hcoLocation => hcoLocation.location?.primary === false) ?? [],
        'location.order',
      ),
      associations: hcp?.associations ?? [],
    })),

  primaryLocation: null,
  otherLocations: [],
  associations: [],
  newsfeed: [],
  setNewsfeed: (newsfeed: Newsfeed[]) => set(() => ({ newsfeed })),
  loading: {
    hcp: false,
    locations: false,
    associations: false,
    newsfeed: false,
    topicScores: false,
    activityScores: false,
    clinicalTrialSponsors: false,
  },
  setLoading: (key: keyof LoadingState, loading: boolean) =>
    set(state => ({ loading: { ...state.loading, [key]: loading } })),
}));

export default useHcpOverviewStore;
